import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './FacultyDirectory.css'; // Import the CSS file

function FacultyDirectory() {
  const [facultyData, setFacultyData] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRank, setSelectedRank] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const departments = {
    'FoC&M': ['Civil Engineering', 'Mechanical Engineering', 'Building & Architectural', 'Industrial & Manufacturing'],
    'FEECE': ['Electrical Engineering', 'Electronic Engineering', 'Telecommunication Eng.', 'Electrical (Automation and Control)', 'Biomedical'],
    'FoCS': ['Computer Systems', 'Software Engineering', 'Artificial Intelligence'],
    'FoS': ['Information Technology', 'Computer Science', 'Cyber Security', 'Data Science', 'English', 'Mathematics & Statistics', 'Physics', 'Chemistry'],
    'FoEE': ['Chemical Engineering', 'Environment Engineering', 'Energy Systems Engineering'],
    'FoT': ['Food Engineering Technology', 'Civil Technology', 'Electrical Technology', 'Mechanical Technology']
  };

  const rankOrder = [
    "Vice Chancellor",
    "Dean",
    "Chairman",
    "Professor",
    "Associate Professor",
    "Assistant Professor",
    "Lecturer",
    "Teaching Assistant",
    "Lab Engineer",
    "Lab Assistant",
    "Lab Incharge",
    "", // Fallback empty rank
  ];

  const getFirstName = (fullName) => {
    const cleanName = fullName
      .replace(/(Prof\.|Dr\.|Dr.-Ing|Engr\.|Mr\.|Ms\.|,|\.)\s?/gi, '') // Remove prefixes
      .replace(/[^\w\s]/g, ''); // Remove special characters
    const parts = cleanName.split(' ');
    return parts[0];
  };

  const sortDirectory = (data) => {
    return data.sort((a, b) => {
      // Sort by currentPortfolio (Vice Chancellor, Dean, Chairman)
      const portfolioA = rankOrder.findIndex(rank => a.currentPortfolio?.includes(rank));
      const portfolioB = rankOrder.findIndex(rank => b.currentPortfolio?.includes(rank));

      if (portfolioA !== portfolioB) {
        return portfolioA - portfolioB; // Higher rank in `rankOrder` comes first
      }

      // Sort by designation if currentPortfolio is identical
      const rankA = rankOrder.findIndex(rank => a.designation?.includes(rank));
      const rankB = rankOrder.findIndex(rank => b.designation?.includes(rank));

      if (rankA !== rankB) {
        return rankA - rankB; // Higher rank in `rankOrder` comes first
      }

      // Alphabetical order for same rank
      return getFirstName(a.name).localeCompare(getFirstName(b.name));
    });
  };

  useEffect(() => {
    fetch('http://121.52.154.120:3001/api/faculty-directory')
      .then(response => response.json())
      .then(data => {
        // Separate Vice Chancellor and sort remaining data
        const viceChancellors = data.filter(member => member.currentPortfolio?.includes("Vice Chancellor"));
        const otherFaculty = data.filter(member => !member.currentPortfolio?.includes("Vice Chancellor"));
        const sortedData = sortDirectory(otherFaculty);

        // Categorize by initials for other faculty
        const categorizedData = sortedData.reduce((acc, member) => {
          const initial = getFirstName(member.name).charAt(0).toUpperCase();
          if (!acc[initial]) acc[initial] = [];
          acc[initial].push(member);
          return acc;
        }, {});

        // Add Vice Chancellor under a special key
        if (viceChancellors.length > 0) {
          categorizedData["Vice Chancellor"] = viceChancellors;
        }

        // Ensure alphabetical order starts from A
        const sortedKeys = Object.keys(categorizedData).sort((a, b) =>
          a === "Vice Chancellor" ? -1 : a.localeCompare(b)
        );

        const sortedCategorizedData = {};
        sortedKeys.forEach((key) => {
          sortedCategorizedData[key] = categorizedData[key];
        });

        setFacultyData(sortedCategorizedData);
      })
      .catch(error => console.error('Error fetching faculty data:', error));
  }, []);

  const filterFacultyData = () => {
    let filteredData = Object.entries(facultyData);

    // Filter by department
    if (selectedDepartment) {
      filteredData = filteredData.map(([initial, members]) => [
        initial,
        members.filter(member => member.department === selectedDepartment),
      ]);
    }

    // Filter by rank (strict match to avoid showing Associate or Assistant Professors in Professors)
    if (selectedRank && selectedRank !== 'All') {
      filteredData = filteredData.map(([initial, members]) => [
        initial,
        members.filter(member => {
          const isExactMatch =
            member.designation === selectedRank ||
            member.currentPortfolio?.includes(selectedRank);
          // Ensure "Professor" does not include "Assistant Professor" or "Associate Professor"
          if (selectedRank === "Professor") {
            return isExactMatch && !member.designation?.includes("Associate") && !member.designation?.includes("Assistant");
          }
          return isExactMatch;
        }),
      ]);
    }

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.map(([initial, members]) => [
        initial,
        members.filter(member =>
          member.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      ]);
    }

    // Remove empty entries
    return filteredData.filter(([_, members]) => members.length > 0);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleRankChange = (event) => {
    setSelectedRank(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearFilter = () => {
    setSelectedDepartment('');
    setSelectedRank('All');
    setSearchTerm('');
  };

  return (
    <div className='faculty-directory'>
      <header className='height-100'>
        <h1 className='text-center fw-semibold'>Faculty Directory</h1>
      </header>
      <div className='container my-1'>
        <div className="filter-section">
          <label htmlFor="faculty-department"></label>
          <select id="department-select" onChange={handleDepartmentChange} className="faculty-department">
            <option value="">Choose Department</option>
            {Object.entries(departments).map(([faculty, deptArray]) => (
              <optgroup label={faculty} key={faculty}>
                {deptArray.map(dept => <option key={dept} value={dept}>{dept}</option>)}
              </optgroup>
            ))}
          </select>
          <select id="rank-select" onChange={handleRankChange} className="rank-filter">
            <option value="All">All Ranks</option>
            {rankOrder.map(rank => rank && (
              <option key={rank} value={rank}>{rank}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          <button onClick={handleClearFilter} className="clear-button">Clear</button>
        </div>

        {filterFacultyData().map(([initial, members], index) => (
          <div key={initial}>
            {/* Show title for Vice Chancellor only when not filtering */}
            {initial === "Vice Chancellor" && selectedRank === 'All' && (
              <h2 className="alphabet-header">Vice Chancellor</h2>
            )}
            {/* For filtered data, show designation title only once */}
            {index === 0 && selectedRank !== 'All' && (
              <h2 className="alphabet-header">{selectedRank}s</h2>
            )}
            {/* For unfiltered data, show alphabet letters */}
            {selectedRank === 'All' && initial !== "Vice Chancellor" && (
              <h2 className="alphabet-header">{initial}</h2>
            )}
            <div className="faculty-row">
              {members.map((member, idx) => (
                <div key={idx} className="faculty-member">
                  <Link to={`/faculty/${member.username || ''}`} className="faculty-link">
                    <h3 className="faculty-name">{member.name || 'Name not available'}</h3>
                    {member.fimages && (
                      <img src={`${member.fimages}`} alt="Faculty" className="faculty-image" />
                    )}
                    <p className="faculty-designation">{member.designation || 'Designation not available'}</p>
                    <p className="faculty-department">{member.department || 'Department not available'}</p>
                    <p className="faculty-currentPortfolio">{member.currentPortfolio || 'Portfolio not available'}</p>
                    <div className="contact-info">
                      <i className="fas fa-envelope"></i>
                      <span className="contact-text"><a href={`mailto:${member.email || ''}`}>{member.email || 'Email not available'}</a></span>
                    </div>
                    <div className="contact-info">
                      <i className="fas fa-phone"></i>
                      <span className="contact-text">{member.phoneNumber || 'Phone number not available'}</span>
                    </div>
                    {member.officeHours && (
                      <p className="faculty-contact"><i className="fa fa-clock"></i> {member.officeHours}</p>
                    )}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FacultyDirectory;
